import { BaseGateway, QueryRequestOptions } from '../BaseGateway';
import { GatewayName } from '../types';
import type {
  CreateSimulationDTO,
  CreateSimulationResponse,
  CreateSimulationScenarioDTO,
  InputsPresignedUploadDTO,
  SimulationDTO,
  SimulationInputsStatus,
  SimulationScenarioDTO,
} from './simulation-types';

type SimulationInputsUploadWaitOptions = QueryRequestOptions & {
  intervalMs?: number;
  timeoutMs?: number;
};

const INPUTS_UPLOAD_ACCEPTED_STATUSES = new Set<SimulationInputsStatus>([
  'UPLOADED',
  'INGESTING',
  'READY',
]);

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type {
  CreateSimulationDTO,
  CreateSimulationResponse,
  CreateSimulationScenarioDTO,
  InputsPresignedUploadDTO,
  RequiredUploadHeaderDTO,
  SimulationDTO,
  SimulationInputsStatus,
  SimulationScenarioDTO,
  SimulationScenarioRunStatus,
  SimulationType,
} from './simulation-types';

export class SimulationGateway extends BaseGateway {
  name: GatewayName = 'simulation';

  async createSimulation(input: CreateSimulationDTO): Promise<CreateSimulationResponse> {
    const { data } = await this.httpPost<CreateSimulationResponse>('/v1/simulations', {
      data: { ...input },
    });
    return data;
  }

  // Presigned S3 PUTs bypass BaseGateway auth and base URL handling.
  async uploadSimulationInputsFile(
    presignedUpload: InputsPresignedUploadDTO,
    file: File,
  ): Promise<void> {
    const headers = new Headers();
    presignedUpload.requiredHeaders.forEach(({ name, value }) => headers.set(name, value));

    const response = await fetch(presignedUpload.url, {
      method: presignedUpload.method,
      headers,
      body: file,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`S3 upload failed (${response.status}): ${detail.slice(0, 200)}`);
    }
  }

  async getSimulations(options?: QueryRequestOptions): Promise<SimulationDTO[]> {
    const { data } = await this.httpGet<{ simulations: SimulationDTO[] }>(
      '/v1/simulations',
      options,
    );
    return data.simulations;
  }

  async getSimulation(
    simulationId: string,
    options?: QueryRequestOptions,
  ): Promise<{ simulation: SimulationDTO; scenarios: SimulationScenarioDTO[] }> {
    const { data } = await this.httpGet<{
      simulation: SimulationDTO;
      scenarios: SimulationScenarioDTO[];
    }>(`/v1/simulations/${simulationId}`, options);
    return data;
  }

  async waitForSimulationInputsUpload(
    simulationId: string,
    options: SimulationInputsUploadWaitOptions = {},
  ): Promise<SimulationDTO> {
    const intervalMs = options.intervalMs ?? 2000;
    const timeoutMs = options.timeoutMs ?? 60_000;
    const start = Date.now();

    while (true) {
      if (options.signal?.aborted) throw new Error('Polling aborted');

      const { simulation } = await this.getSimulation(simulationId, { signal: options.signal });

      if (INPUTS_UPLOAD_ACCEPTED_STATUSES.has(simulation.inputsStatus)) {
        return simulation;
      }

      if (simulation.inputsStatus === 'FAILED') {
        throw new Error('Inputs upload was rejected by the server (FAILED).');
      }

      if (Date.now() - start > timeoutMs) {
        throw new Error(
          `Timed out waiting for inputs to upload (still ${
            simulation.inputsStatus
          } after ${Math.round(timeoutMs / 1000)}s).`,
        );
      }

      await sleep(intervalMs);
    }
  }

  async createSimulationScenario(
    simulationId: string,
    input: CreateSimulationScenarioDTO,
  ): Promise<SimulationScenarioDTO> {
    const { data } = await this.httpPost<{ scenario: SimulationScenarioDTO }>(
      `/v1/simulations/${simulationId}/scenarios`,
      { data: { ...input } },
    );
    return data.scenario;
  }
}
